import type {
  YardCompanyStepExceptionConfiguration,
  YardPaginatedResponse,
  YardPaginatedSearch,
  YardStepCatalogItem,
} from 'contracts/VehicleJourney';
import { useDebounce } from 'hooks/useDebounce';
import { AlertTriangle, Check, Info, Search, X } from 'lucide-react';
import React, {
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { apiErrorHandler, notify } from 'services';
import { Formatter } from 'utils/Formatter';
import { STEP_TYPE_LABELS } from './config';

interface Props {
  getCompanies: (
    params: YardPaginatedSearch
  ) => Promise<YardPaginatedResponse<YardCompanyStepExceptionConfiguration>>;
  getSteps: (
    params: YardPaginatedSearch
  ) => Promise<YardPaginatedResponse<YardStepCatalogItem>>;
  replaceExceptions: (
    companyId: number,
    yardStepIds: number[]
  ) => Promise<void>;
}

const PAGE_SIZE = 20;
const normalizeIds = (ids: number[]) => [...ids].sort((a, b) => a - b);
const sameIds = (first: number[], second: number[]) =>
  first.join(',') === second.join(',');
const reachedScrollEnd = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight < 48;

export const CustomerExceptionsTab: React.FC<Props> = ({
  getCompanies,
  getSteps,
  replaceExceptions,
}) => {
  const [companies, setCompanies] = useState<
    YardCompanyStepExceptionConfiguration[]
  >([]);
  const [steps, setSteps] = useState<YardStepCatalogItem[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [savedByCompany, setSavedByCompany] = useState<
    Record<number, number[]>
  >({});
  const [draftByCompany, setDraftByCompany] = useState<
    Record<number, number[]>
  >({});
  const [companySearch, setCompanySearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const debouncedCompanySearch = useDebounce(companySearch, 300);
  const debouncedStepSearch = useDebounce(stepSearch, 300);
  const [companyMeta, setCompanyMeta] = useState<
    YardPaginatedResponse<unknown>['meta'] | null
  >(null);
  const [stepMeta, setStepMeta] = useState<
    YardPaginatedResponse<unknown>['meta'] | null
  >(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingMoreCompanies, setIsLoadingMoreCompanies] = useState(false);
  const [isLoadingSteps, setIsLoadingSteps] = useState(true);
  const [isLoadingMoreSteps, setIsLoadingMoreSteps] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [stepError, setStepError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [companyReloadKey, setCompanyReloadKey] = useState(0);
  const [stepReloadKey, setStepReloadKey] = useState(0);
  const companyRequestId = useRef(0);
  const stepRequestId = useRef(0);
  const savedByCompanyRef = useRef<Record<number, number[]>>({});
  const draftByCompanyRef = useRef<Record<number, number[]>>({});
  const isLoadingCompaniesRef = useRef(true);
  const isLoadingMoreCompaniesRef = useRef(false);
  const isLoadingStepsRef = useRef(true);
  const isLoadingMoreStepsRef = useRef(false);

  const loadCompanies = useCallback(
    async (page: number, append: boolean, search: string) => {
      const requestId = ++companyRequestId.current;
      if (append) {
        isLoadingMoreCompaniesRef.current = true;
        setIsLoadingMoreCompanies(true);
      } else {
        isLoadingCompaniesRef.current = true;
        isLoadingMoreCompaniesRef.current = false;
        setIsLoadingCompanies(true);
        setIsLoadingMoreCompanies(false);
        setCompanyMeta(null);
        setCompanies([]);
        setSelectedCompanyId(null);
        setCompanyError(false);
      }

      try {
        const response = await getCompanies({
          page,
          limit: PAGE_SIZE,
          search,
        });
        if (requestId !== companyRequestId.current) return;

        setCompanies((current) =>
          append ? [...current, ...response.data] : response.data
        );
        setCompanyMeta(response.meta);
        const nextSaved = { ...savedByCompanyRef.current };
        const nextDraft = { ...draftByCompanyRef.current };
        response.data.forEach((company) => {
          const companyId = company.companyId;
          const previousSaved = savedByCompanyRef.current[companyId];
          const previousDraft = draftByCompanyRef.current[companyId];
          const hasLocalChanges =
            previousSaved !== undefined &&
            previousDraft !== undefined &&
            !sameIds(previousSaved, previousDraft);
          const receivedIds = normalizeIds(company.yardStepIds);

          nextSaved[companyId] = receivedIds;
          if (!hasLocalChanges) nextDraft[companyId] = receivedIds;
        });
        savedByCompanyRef.current = nextSaved;
        draftByCompanyRef.current = nextDraft;
        setSavedByCompany(nextSaved);
        setDraftByCompany(nextDraft);
        if (!append) setSelectedCompanyId(response.data[0]?.companyId ?? null);
      } catch (error) {
        if (requestId !== companyRequestId.current) return;
        if (!append) setCompanyError(true);
        notify('error', apiErrorHandler(error).errorMessage);
      } finally {
        if (requestId === companyRequestId.current) {
          isLoadingCompaniesRef.current = false;
          isLoadingMoreCompaniesRef.current = false;
          setIsLoadingCompanies(false);
          setIsLoadingMoreCompanies(false);
        }
      }
    },
    [getCompanies]
  );

  const loadSteps = useCallback(
    async (page: number, append: boolean, search: string) => {
      const requestId = ++stepRequestId.current;
      if (append) {
        isLoadingMoreStepsRef.current = true;
        setIsLoadingMoreSteps(true);
      } else {
        isLoadingStepsRef.current = true;
        isLoadingMoreStepsRef.current = false;
        setIsLoadingSteps(true);
        setIsLoadingMoreSteps(false);
        setStepMeta(null);
        setSteps([]);
        setStepError(false);
      }

      try {
        const response = await getSteps({
          page,
          limit: PAGE_SIZE,
          search,
        });
        if (requestId !== stepRequestId.current) return;
        setSteps((current) =>
          append ? [...current, ...response.data] : response.data
        );
        setStepMeta(response.meta);
      } catch (error) {
        if (requestId !== stepRequestId.current) return;
        if (!append) setStepError(true);
        notify('error', apiErrorHandler(error).errorMessage);
      } finally {
        if (requestId === stepRequestId.current) {
          isLoadingStepsRef.current = false;
          isLoadingMoreStepsRef.current = false;
          setIsLoadingSteps(false);
          setIsLoadingMoreSteps(false);
        }
      }
    },
    [getSteps]
  );

  useEffect(() => {
    loadCompanies(1, false, debouncedCompanySearch);
  }, [companyReloadKey, debouncedCompanySearch, loadCompanies]);

  useEffect(() => {
    loadSteps(1, false, debouncedStepSearch);
  }, [stepReloadKey, debouncedStepSearch, loadSteps]);

  const selectedCompany = companies.find(
    ({ companyId }) => companyId === selectedCompanyId
  );
  const selectedIds = selectedCompanyId
    ? draftByCompany[selectedCompanyId] || []
    : [];
  const savedIds = selectedCompanyId
    ? savedByCompany[selectedCompanyId] || []
    : [];
  const isDirty = selectedIds.join(',') !== savedIds.join(',');

  const toggleStep = (stepId: number) => {
    if (!selectedCompanyId) return;
    setDraftByCompany((current) => {
      const selected = current[selectedCompanyId] || [];
      const next = selected.includes(stepId)
        ? selected.filter((id) => id !== stepId)
        : [...selected, stepId];
      const updated = {
        ...current,
        [selectedCompanyId]: normalizeIds(next),
      };
      draftByCompanyRef.current = updated;
      return updated;
    });
    setSaveError(false);
  };

  const clearSelection = () => {
    if (!selectedCompanyId) return;
    setDraftByCompany((current) => {
      const updated = { ...current, [selectedCompanyId]: [] };
      draftByCompanyRef.current = updated;
      return updated;
    });
    setSaveError(false);
  };

  const save = async () => {
    if (!selectedCompanyId || !isDirty || isSaving) return;
    setIsSaving(true);
    setSaveError(false);
    try {
      await replaceExceptions(selectedCompanyId, selectedIds);
      setSavedByCompany((current) => {
        const updated = { ...current, [selectedCompanyId]: selectedIds };
        savedByCompanyRef.current = updated;
        return updated;
      });
      setCompanies((current) =>
        current.map((company) =>
          company.companyId === selectedCompanyId
            ? {
                ...company,
                yardStepIds: selectedIds,
                ignoredStepCount: selectedIds.length,
              }
            : company
        )
      );
      notify('success', 'Exceções por cliente salvas com sucesso');
    } catch (error) {
      setSaveError(true);
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const loadMoreCompanies = (event: UIEvent<HTMLDivElement>) => {
    if (
      reachedScrollEnd(event.currentTarget) &&
      companyMeta &&
      companyMeta.currentPage < companyMeta.lastPage &&
      !isLoadingCompaniesRef.current &&
      !isLoadingMoreCompaniesRef.current
    ) {
      loadCompanies(companyMeta.currentPage + 1, true, debouncedCompanySearch);
    }
  };

  const loadMoreSteps = (event: UIEvent<HTMLDivElement>) => {
    if (
      reachedScrollEnd(event.currentTarget) &&
      stepMeta &&
      stepMeta.currentPage < stepMeta.lastPage &&
      !isLoadingStepsRef.current &&
      !isLoadingMoreStepsRef.current
    ) {
      loadSteps(stepMeta.currentPage + 1, true, debouncedStepSearch);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-205px)] flex-1 flex-col overflow-hidden md:flex-row">
      <aside className="flex h-80 w-full shrink-0 flex-col border-b border-slate-200 bg-white md:h-auto md:w-80 md:border-b-0 md:border-r">
        <div className="border-b border-slate-200 p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Clientes</h2>
            {companyMeta && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                {companyMeta.total} cadastrados
              </span>
            )}
          </div>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
              size={15}
            />
            <input
              aria-label="Pesquisar clientes"
              className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
              onChange={(event) => setCompanySearch(event.target.value)}
              placeholder="Pesquisar por nome ou CNPJ..."
              value={companySearch}
            />
          </div>
        </div>
        <div
          aria-label="Lista de clientes"
          className="flex-1 overflow-y-auto md:max-h-[560px] md:flex-none"
          onScroll={loadMoreCompanies}
        >
          {isLoadingCompanies ? (
            <p className="p-4 text-center text-xs text-slate-500">
              Carregando clientes...
            </p>
          ) : companyError ? (
            <div className="p-4 text-center" role="alert">
              <p className="text-xs font-semibold text-red-600">
                Não foi possível carregar os clientes.
              </p>
              <button
                className="mt-3 text-xs font-bold text-[#EE7C2C]"
                onClick={() => setCompanyReloadKey((current) => current + 1)}
                type="button"
              >
                Tentar novamente
              </button>
            </div>
          ) : companies.length ? (
            <>
              {companies.map((company) => {
                const isSelected = company.companyId === selectedCompanyId;
                return (
                  <button
                    aria-pressed={isSelected}
                    className={`relative flex w-full items-center justify-between gap-3 border-b border-slate-200 p-3 text-left transition-colors md:h-14 ${
                      isSelected ? 'bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                    key={company.companyId}
                    onClick={() => {
                      setSelectedCompanyId(company.companyId);
                      setSaveError(false);
                    }}
                    type="button"
                  >
                    {isSelected && (
                      <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
                    )}
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-slate-800">
                        {company.tradeName}
                      </strong>
                      <span className="mt-0.5 block truncate text-[11px] font-semibold text-slate-500">
                        {Formatter.document(
                          company.document,
                          company.documentType
                        )}
                      </span>
                    </span>
                    <span
                      aria-label={`${savedByCompany[company.companyId]?.length || 0} etapas ignoradas`}
                      className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600"
                    >
                      {savedByCompany[company.companyId]?.length || 0}
                    </span>
                  </button>
                );
              })}
              {isLoadingMoreCompanies && (
                <p className="p-3 text-center text-xs text-slate-500">
                  Carregando mais clientes...
                </p>
              )}
            </>
          ) : (
            <p className="p-4 text-center text-xs text-slate-500">
              Nenhum cliente encontrado.
            </p>
          )}
        </div>
      </aside>

      <section className="flex-1 overflow-y-auto bg-slate-50 p-4 md:p-6">
        {selectedCompany ? (
          <div className="mx-auto max-w-6xl overflow-hidden rounded border border-slate-200 bg-white shadow-sm">
            <header className="flex flex-col justify-between gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-start">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Etapas ignoradas
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Selecione as etapas que não devem fazer parte das novas
                  jornadas deste cliente.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                    Cliente: {selectedCompany.tradeName}
                  </span>
                  <span className="font-semibold text-slate-500">
                    {Formatter.document(
                      selectedCompany.document,
                      selectedCompany.documentType
                    )}
                  </span>
                  {isDirty && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                      Alterações não salvas
                    </span>
                  )}
                </div>
              </div>
              <button
                className="flex items-center justify-center gap-2 rounded bg-[#EE7C2C] px-5 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!isDirty || isSaving}
                onClick={save}
                type="button"
              >
                <Check size={17} /> {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </header>

            <div className="space-y-4 p-5">
              <div
                className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-4 text-sm leading-5 text-sky-900"
                role="note"
              >
                <Info
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 text-sky-600"
                  size={20}
                />
                <p>
                  As etapas selecionadas <strong>não serão adicionadas</strong>{' '}
                  às novas jornadas deste cliente. Jornadas já confirmadas não
                  serão alteradas.
                </p>
              </div>

              {saveError && (
                <div
                  className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-700"
                  role="alert"
                >
                  <AlertTriangle size={16} /> Não foi possível salvar. Tente
                  novamente.
                </div>
              )}

              <div className="relative max-w-sm">
                <Search
                  aria-hidden="true"
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                  size={15}
                />
                <input
                  aria-label="Filtrar etapas"
                  className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
                  onChange={(event) => setStepSearch(event.target.value)}
                  placeholder="Filtrar etapas..."
                  value={stepSearch}
                />
              </div>

              <div className="overflow-hidden rounded border border-slate-200">
                <div
                  aria-label="Lista de etapas"
                  className="max-h-[420px] overflow-auto"
                  onScroll={loadMoreSteps}
                >
                  <table className="w-full min-w-[620px] text-left text-sm">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="w-12 px-4 py-3" scope="col">
                          <span className="sr-only">Ignorar</span>
                        </th>
                        <th className="px-4 py-3" scope="col">
                          Etapa
                        </th>
                        <th className="px-4 py-3" scope="col">
                          Código
                        </th>
                        <th className="px-4 py-3 text-right" scope="col">
                          Tipo
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {steps.map((step) => {
                        const checked = selectedIds.includes(step.id);
                        return (
                          <tr
                            className={
                              checked ? 'bg-orange-50' : 'hover:bg-slate-50'
                            }
                            key={step.id}
                          >
                            <td className="px-4 py-3 text-center">
                              <input
                                aria-label={`Ignorar etapa ${step.name}`}
                                checked={checked}
                                className="h-4 w-4 accent-[#EE7C2C]"
                                onChange={() => toggleStep(step.id)}
                                type="checkbox"
                              />
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {step.name}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500">
                              {step.code}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                {STEP_TYPE_LABELS[step.type]}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {isLoadingSteps && (
                    <p className="p-6 text-center text-xs text-slate-500">
                      Carregando etapas...
                    </p>
                  )}
                  {!isLoadingSteps && stepError && (
                    <div className="p-5 text-center" role="alert">
                      <p className="text-xs font-semibold text-red-600">
                        Não foi possível carregar as etapas.
                      </p>
                      <button
                        className="mt-3 text-xs font-bold text-[#EE7C2C]"
                        onClick={() =>
                          setStepReloadKey((current) => current + 1)
                        }
                        type="button"
                      >
                        Tentar novamente
                      </button>
                    </div>
                  )}
                  {!isLoadingSteps && !stepError && !steps.length && (
                    <p className="p-6 text-center text-xs text-slate-500">
                      Nenhuma etapa encontrada.
                    </p>
                  )}
                  {isLoadingMoreSteps && (
                    <p className="p-3 text-center text-xs text-slate-500">
                      Carregando mais etapas...
                    </p>
                  )}
                </div>
                <footer className="flex flex-col justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs sm:flex-row sm:items-center">
                  <p className="font-semibold text-slate-700">
                    {selectedIds.length} de {stepMeta?.total ?? 0} etapas serão
                    ignoradas
                  </p>
                  <button
                    className="flex items-center gap-1 text-xs font-bold text-slate-600 disabled:opacity-50"
                    disabled={!selectedIds.length || isSaving}
                    onClick={clearSelection}
                    type="button"
                  >
                    <X size={14} /> Limpar seleção
                  </button>
                </footer>
              </div>
            </div>
          </div>
        ) : (
          !isLoadingCompanies &&
          !companyError && (
            <div className="flex min-h-64 items-center justify-center rounded border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              Selecione um cliente para configurar as etapas ignoradas.
            </div>
          )
        )}
      </section>
    </div>
  );
};
